import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { Chart, elements, registerables } from 'chart.js';
import { BeatLoader } from 'react-spinners';
import '../../../../styles/count_graphs.css'
import { BiFullscreen, BiExitFullscreen, BiLineChart, BiBarChartAlt2 } from 'react-icons/bi';


const WeeklyInfringementChart = ({ 
    selectedYear,
    weeklyInfringementData,
    weeklyDataLoading,
    selectedMonth,
    selectedWeek,
    handleWeeklyBarClick,
    totalWeeklyInfringements,
    weeklyPercentageIncrease 
}) => {

    const [barChartData, setBarChartData] = useState();
    const [lineChartData, setLineChartData] = useState();
    const [chartType, setChartType] = useState('bar');
    const barChartRef = useRef(null);
    const lineChartRef = useRef(null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const [selectedWeekOfMonth, setSelectedWeekOfMonth] = useState();

    const getMonthName = (monthNumber) => {
        const months = [
            "Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];
        return months[monthNumber - 1];
    };

    const smallScreen = window.innerWidth < 786;

    const prepareWeeklyInfringementBarChart = () => {
        const transformedWeekLabels = [];

        for (let i = 1; i <= weeklyInfringementData.length; i++) {
            transformedWeekLabels.push(i);
            if (weeklyInfringementData[i - 1]?.week === selectedWeek) {
                setSelectedWeekOfMonth(i);
            }
        }

        return {
            labels: transformedWeekLabels.map(d => `Week ${d}`),
            datasets: [
                {
                    label: 'Weekly Infringements',
                    data: weeklyInfringementData.map(d => d.total_infringements),
                    backgroundColor: '#FFC400',
                    borderWidth: {
                        top: 0,
                        right: weeklyInfringementData.map(d => (selectedWeek === d.week ? 2 : 0)),
                        bottom: 0,
                        left: weeklyInfringementData.map(d => (selectedWeek === d.week ? 2 : 0)),
                    },

                }
            ]
            
        };
    };

    const getWeeklyInfringementLineChartOptions = () => {
        return {
            responsive: true, // Ensure the chart is responsive
            maintainAspectRatio: true, // Ensure the chart maintains its aspect ratio
            hover: {
                mode: 'index',
                intersect: false,
            },
            onHover: function (event, elements) {
                const chartElement = this.canvas;
                if (elements && elements.length > 0) {
                    chartElement.style.cursor = 'pointer';
                } else {
                    chartElement.style.cursor = 'default';
                }
            },
            elements: {
                points: {
                    radius: 4,
                    hoverRadius: 6
                }
            },
            scales: {
                x: {
                    ticks: {
                        color: (context) => {
                            const labelWeek = weeklyInfringementData[context.index]?.week;
                            return labelWeek === selectedWeek ? '#32de84' : '#fff';
                        },
                        font: {
                            weight: (context) => {
                                const labelWeek = weeklyInfringementData[context.index]?.week;
                                return labelWeek === selectedWeek ? 'bold' : 'normal';
                            },
                            size: (context) => {
                                const labelWeek = weeklyInfringementData[context.index]?.week;
                                return labelWeek === selectedWeek ? 14 : 12;
                            }
                        }
                    },
                    title: {
                        display: true,
                        text: 'Weeks',
                    },
                },
                y: {
                    title: {
                        display: true,
                        text: 'Infringement Count',
                    },
                },
            },
            plugins: {
                tooltip: {
                    mode: 'nearest', // Show tooltip for all datasets at the same index
                    intersect: false,
                },
                legend: {
                    position: 'top', // Position the legend at the top
                    labels: {
                        font: {
                            size: smallScreen ? 8 : 14, // Adjust font size of legend labels
                        },
                        boxWidth: smallScreen ? 8 : 40,  // Adjust width of legend marker box
                        boxHeight: smallScreen ? 8 : 10, // Adjust height of legend marker box (optional, defaults to boxWidth)
                    },
                },
            },

            onClick: (event, elements) => {
                if (elements.length) {
                    const index = elements[0].index;
                    handleWeeklyBarClick(weeklyInfringementData[index].week);
                }
            },

        }

    }
    
    const getWeeklyInfringementBarChartOptions = () => {
        return {
            responsive: true, // Ensure the chart is responsive
            maintainAspectRatio: true, // Ensure the chart maintains its aspect ratio
            hover: {
                mode: 'index',
                intersect: false,
            },
            onHover: function (event, elements) {
                const chartElement = this.canvas;
                if (elements && elements.length > 0) {
                    chartElement.style.cursor = 'pointer';
                } else {
                    chartElement.style.cursor = 'default';
                }
            },
            scales: {
                x: {
                    ticks: {
                        color: (context) => {
                            const labelWeek = weeklyInfringementData[context.index]?.week;
                            return labelWeek === selectedWeek ? '#32de84' : '#fff';
                        },
                        font: {
                            weight: (context) => {
                                const labelWeek = weeklyInfringementData[context.index]?.week;
                                return labelWeek === selectedWeek ? 'bold' : 'normal';
                            },
                            size: (context) => {
                                const labelWeek = weeklyInfringementData[context.index]?.week;
                                return labelWeek === selectedWeek ? 14 : 12;
                            }
                        }
                    },
                    title: {
                        display: true,
                        text: 'Weeks',
                    },
                    stacked: true, // Enable stacking on the x-axis
                },
                y: {
                    title: {
                        display: true,
                        text: 'Infringement Count',
                    },
                    stacked: true, // Enable stacking on the y-axis
                },
            },
            plugins: {
                tooltip: {
                    mode: 'nearest', // Show tooltip for all datasets at the same index
                    intersect: false
                },
                legend: {
                    position: 'top', // Position the legend at the top
                    labels: {
                        font: {
                            size: smallScreen ? 8 : 14, // Adjust font size of legend labels
                        },
                        boxWidth: smallScreen ? 8 : 40,  // Adjust width of legend marker box
                        boxHeight: smallScreen ? 8 : 10, // Adjust height of legend marker box (optional, defaults to boxWidth)
                    },
                },
            },

            onClick: (event, elements) => {
                if (elements.length) {
                    const index = elements[0].index;
                    handleWeeklyBarClick(weeklyInfringementData[index].week);
                }
            },

        }
    }

    const prepareWeeklyInfringementLineChart = () => {
        const transformedWeekLabels = [];

        for (let i = 1; i <= weeklyInfringementData.length; i++) {
            transformedWeekLabels.push(i);
            if (weeklyInfringementData[i - 1]?.week === selectedWeek) {
                setSelectedWeekOfMonth(i);
            }
        }

        return {
            labels: transformedWeekLabels.map(d => `Week ${d}`),
            datasets: [
                {
                    label: 'Weekly Infringements',
                    data: weeklyInfringementData.map(d => d.total_infringements),
                    backgroundColor: '#FFC400',
                    borderColor: '#FFC400',
                    borderWidth: 3,
                    tension: 0.3
                }
            ]
        };
    };

    const toggleChartType = () => {
        setChartType(prevType => prevType === 'bar' ? 'line' : 'bar');
    };

    useEffect(() => {
        if (weeklyInfringementData.length) {
            const preparedBarChart = prepareWeeklyInfringementBarChart();
            if (preparedBarChart) {
                setBarChartData(preparedBarChart);
            }
            const preparedLineChart = prepareWeeklyInfringementLineChart();
            if (preparedLineChart) {
                setLineChartData(preparedLineChart);
            }
        }
    }, [weeklyInfringementData]);


    Chart.register(...registerables );

    return (
        <>
            <div className={`count-bar-chart-wrapper ${isFullscreen ? 'fullscreen-container' : ''}`}>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', }}>
                    {/* Left-aligned title */}
                    <div className='graph-title' style={{ flex: '1 1 auto', minWidth: '200px' }}>
                        Weekly Infringements
                    </div>

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        justifyContent: 'flex-end',
                        flex: '1 1 auto',
                        minWidth: '100px',
                    }}>      
                        <button className='toggle-chart-button' onClick={() => toggleChartType('month')}>
                            {chartType ? (
                            <BiBarChartAlt2 size={24} title='Switch to Bar Chart' color='white' />
                            ) : (
                            <BiLineChart size={24} title='Switch to Line Chart' color='white' />
                            )}
                        </button>

                        <button
                            className='toggle-chart-button'
                            onClick={() => setIsFullscreen(prev => !prev)}
                            title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                        >
                            {isFullscreen ? (
                            <BiExitFullscreen size={24} color="white" />
                            ) : (
                            <BiFullscreen size={24} color="white" />
                            )}
                        </button>
                        <div className='count-chart-footer-percentage-container'>
                            <div className='lpr-chart-footer-percentage-icon'>
                                {
                                    weeklyPercentageIncrease > 0 ? <svg style={{ color: '#32de84' }} xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-up-right-circle" viewBox="0 0 16 16">
                                        <path fillRule="evenodd" d="M1 8a7 7 0 1 0 14 0A7 7 0 0 0 1 8zm15 0A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.854 10.803a.5.5 0 1 1-.708-.707L9.243 6H6.475a.5.5 0 1 1 0-1h3.975a.5.5 0 0 1 .5.5v3.975a.5.5 0 1 1-1 0V6.707l-4.096 4.096z" />
                                    </svg> :
                                        <svg style={{ color: '#EF5B5B' }} xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-down-right-circle" viewBox="0 0 16 16">
                                            <path fillRule="evenodd" d="M1 8a7 7 0 1 0 14 0A7 7 0 0 0 1 8zm15 0A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.854 5.146a.5.5 0 1 0-.708.708L9.243 9.95H6.475a.5.5 0 1 0 0 1h3.975a.5.5 0 0 0 .5-.5V6.475a.5.5 0 1 0-1 0v2.768L5.854 5.146z" />
                                        </svg>
                                }

                            </div>
                            <div className='lpr-chart-footer-percentage-value' style={{ color: weeklyPercentageIncrease > 0 ? '#32de84' : '#EF5B5B' }}>{weeklyPercentageIncrease}%</div>
                        </div>
                    </div>
                </div>  


                <div className={weeklyDataLoading ? 'lpr-weekly-bar-chart-container-loading' : ''}>
                    {
                        chartType === 'bar' ?
                            <Bar
                                ref={barChartRef}
                                data={barChartData || { labels: [], datasets: [] }} // Fetch prepared data
                                options={getWeeklyInfringementBarChartOptions()}
                            />
                            :
                            <Line 
                                ref={lineChartRef}
                                data={lineChartData || { labels: [], datasets: [] }} // Fetch prepared data
                                options={getWeeklyInfringementLineChartOptions()}
                            />

                    }

                    {
                        weeklyDataLoading && <div id="lpr-weekly-loading-overlay">
                            <BeatLoader color={'white'} loading={weeklyDataLoading} size={15} />
                        </div>
                    }
                </div>

            </div>


        </>
    )
}


export default WeeklyInfringementChart;